(() => {
  const rootElement = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let revealObserver;
  let parallaxItems = [];
  let parallaxFrame = 0;

  rootElement.classList.add('rv-motion-ready');

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const getScope = (root) => (root && root.querySelectorAll ? root : document);

  const prepareGlobalMotion = (root = document) => {
    const scope = getScope(root);

    scope.querySelectorAll('#MainContent > .shopify-section').forEach((section) => {
      if (section.querySelector('.rv-home, .rv-page')) return;
      const target = section.querySelector(
        '.section-content-wrapper, .product-information, .main-collection-grid, .collection-wrapper, .cart-page, .search-results'
      );
      if (target && !target.hasAttribute('data-rv-reveal')) target.setAttribute('data-rv-reveal', '');
    });

    scope.querySelectorAll('.product-grid, .collection-list, .resource-list').forEach((grid) => {
      if (!grid.hasAttribute('data-rv-stagger')) grid.setAttribute('data-rv-stagger', '');
      grid.querySelectorAll('.product-card, .collection-card, .resource-card').forEach((card) => {
        if (!card.hasAttribute('data-rv-stagger-item')) card.setAttribute('data-rv-stagger-item', '');
      });
    });
  };

  const revealNow = (items) => {
    items.forEach((item) => item.classList.add('rv-visible'));
  };

  const initialiseReveal = (root = document) => {
    const scope = getScope(root);
    const items = [...scope.querySelectorAll('[data-rv-reveal], [data-rv-stagger-item]')].filter(
      (item) => !item.classList.contains('rv-visible') && !item.dataset.rvRevealReady
    );

    scope.querySelectorAll('[data-rv-stagger]').forEach((group) => {
      [...group.querySelectorAll('[data-rv-stagger-item]')].forEach((item, index) => {
        item.style.setProperty('--rv-delay', `${Math.min(index * 70, 420)}ms`);
      });
    });

    if (!items.length) return;
    items.forEach((item) => {
      item.dataset.rvRevealReady = 'true';
    });

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      revealNow(items);
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('rv-visible');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -7% 0px', threshold: 0.08 }
      );
    }

    items.forEach((item) => revealObserver.observe(item));
  };

  const collectParallaxItems = () => {
    parallaxItems = [...document.querySelectorAll('[data-rv-parallax]')];
  };

  const updateParallax = () => {
    parallaxFrame = 0;
    if (reduceMotion.matches || !parallaxItems.length) return;

    const viewportCenter = window.innerHeight / 2;
    parallaxItems.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.bottom < -120 || rect.top > window.innerHeight + 120) return;

      const speed = Number.parseFloat(item.dataset.rvParallax || '0.04');
      const itemCenter = rect.top + rect.height / 2;
      const offset = clamp((viewportCenter - itemCenter) * speed, -38, 38);
      item.style.setProperty('--rv-parallax-y', `${offset.toFixed(2)}px`);
    });
  };

  const requestParallaxUpdate = () => {
    if (parallaxFrame) return;
    parallaxFrame = window.requestAnimationFrame(updateParallax);
  };

  const initialiseParallax = () => {
    collectParallaxItems();
    requestParallaxUpdate();
  };

  const initialiseTilt = (root = document) => {
    const scope = getScope(root);
    scope.querySelectorAll('[data-rv-tilt]:not([data-rv-tilt-ready])').forEach((item) => {
      item.dataset.rvTiltReady = 'true';

      const reset = () => {
        item.style.setProperty('--rv-tilt-x', '0deg');
        item.style.setProperty('--rv-tilt-y', '0deg');
        item.style.setProperty('--rv-lift', '0px');
        item.style.setProperty('--rv-pointer-x', '50%');
        item.style.setProperty('--rv-pointer-y', '50%');
      };

      item.addEventListener('pointermove', (event) => {
        if (reduceMotion.matches || event.pointerType === 'touch') return;
        const rect = item.getBoundingClientRect();
        const x = clamp((event.clientX - rect.left) / rect.width, 0, 1);
        const y = clamp((event.clientY - rect.top) / rect.height, 0, 1);
        item.style.setProperty('--rv-tilt-x', `${((0.5 - y) * 2.2).toFixed(2)}deg`);
        item.style.setProperty('--rv-tilt-y', `${((x - 0.5) * 2.8).toFixed(2)}deg`);
        item.style.setProperty('--rv-lift', '-4px');
        item.style.setProperty('--rv-pointer-x', `${(x * 100).toFixed(1)}%`);
        item.style.setProperty('--rv-pointer-y', `${(y * 100).toFixed(1)}%`);
      });

      item.addEventListener('pointerleave', reset);
      item.addEventListener('pointercancel', reset);
      reset();
    });
  };

  const updateHeaderState = () => {
    document.querySelectorAll('.header-section').forEach((header) => {
      header.classList.toggle('rv-header-scrolled', window.scrollY > 14);
    });
  };

  const initialise = (root = document) => {
    prepareGlobalMotion(root);
    initialiseReveal(root);
    initialiseTilt(root);
    initialiseParallax();
    updateHeaderState();
  };

  const onScroll = () => {
    requestParallaxUpdate();
    updateHeaderState();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(document), { once: true });
  } else {
    initialise(document);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', requestParallaxUpdate, { passive: true });
  reduceMotion.addEventListener?.('change', () => initialise(document));

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:reorder', () => initialise(document));
})();
